import { memo, useCallback, useMemo } from "react";
import { generatePath } from "react-router";

import { PATHS } from "@/routes/paths";

import { StockTableRowDailyItem } from "@/components/features/items/stock/_shared/components/table/row/daily-item";
import { StockTableRow } from "@/components/features/items/stock/_shared/components/table/row/row";
import type { RoomStockDiffStore } from "@/components/features/items/stock/room/model/usecase/use-create-form-model/room-stock-diff-store";
import type { ServerModel } from "@/components/features/items/stock/room/model/usecase/use-create-form-model/type";

import { Label } from "@/components/ui/label/label";
import { TableBody } from "@/components/ui/table/table";

type Props = {
	items: ServerModel["roomStock"]["items"];
	hotelID: string;
	roomStockDiffStore: RoomStockDiffStore;
};

export const RoomStockTableRows = memo(({ items, hotelID, roomStockDiffStore }: Props) => {
	const onSetStock = useCallback(
		(roomID: string, date: string) => (isAvailable: boolean, remainNum: number) => {
			roomStockDiffStore.put({ roomID, date, isAvailable, remainNum });
		},
		[roomStockDiffStore],
	);

	const roomPriceEditPagePath = useMemo(() => {
		return generatePath(PATHS.HOTEL.ITEMS.ROOM.PRICE_RANK, {
			hotelID,
		});
	}, [hotelID]);

	return (
		<TableBody>
			{items.map((item) => {
				const { roomID, name, isInSale, isDummyRoom } = item;

				const itemEditPagePath = isDummyRoom
					? generatePath(PATHS.HOTEL.ITEMS.ROOM.EDIT.ROOM_ID.DUMMY, {
							hotelID,
							roomID,
						})
					: generatePath(PATHS.HOTEL.ITEMS.ROOM.EDIT.ROOM_ID.DEFAULT, {
							hotelID,
							roomID,
						});

				return (
					<StockTableRow
						key={roomID}
						itemName={name}
						itemEditPagePath={itemEditPagePath}
						itemLabelChildren={
							<>
								{isDummyRoom && <Label variant={"pink"} text={"ダミー部屋"} size={"xs"} />}
								{!isInSale && <Label variant={"black"} text={"非公開"} size={"xs"} />}
							</>
						}
						stockDailyItemChildren={
							<>
								{item.stocks.map((stock, idx) => {
									return (
										<StockTableRowDailyItem
											key={`${roomID}-${stock.date}-${idx}`}
											stockDailyItem={stock}
											onSetStock={onSetStock(roomID, stock.date)}
											roomPriceEditPagePath={roomPriceEditPagePath}
											currentStockDailyItem={roomStockDiffStore.getCurrentRoomStock({
												roomID,
												date: stock.date,
												isAvailable: stock.isAvailable,
												remainNum: stock.remainNum,
											})}
										/>
									);
								})}
							</>
						}
					/>
				);
			})}
		</TableBody>
	);
});

RoomStockTableRows.displayName = "RoomStockTableRows";
